/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    RECENT_BROADCASTS_TABLE_LAYOUT
 * Date:         2026-02-10
 * Modified By:  Claude
 *
 * Description:
 * Redesigned to HTML table layout per broadcast-item-detail.html mockup.
 * Displays seller's recent broadcasts with type, product, condition, qty, date, price.
 *
 * Related Screens / Modules:
 * - client/src/pages/ItemDetailPage/ItemDetailPage.tsx
 * - client/src/pages/ItemDetailPage/interfaces/itemDetail.ts
 * - client/src/services/broadcasts.ts
 */

import React, { useState, useEffect } from "react";
import { BroadcastItem } from "../interfaces/itemDetail";
import { BroadcastItem as BroadcastItemType, BroadcastGroup } from "../../../interfaces/broadcast";
import { listAllBroadcastsWithUsers } from "../../../services/broadcasts";
import { getErrorMessage } from "../../../utils/notifications";
import "./RecentBroadcasts.scss";

// [2025-01-12] Item detail: Component for displaying seller's recent broadcasts

// Flattened broadcast item for display
interface FlattenedBroadcastItem extends BroadcastItemType {
  broadcastType: "WTB" | "WTS";
  broadcastId: string;
  createdAt: string;
  sentAt: string | null;
}

// Helper functions for condition badge colors
const getConditionBackgroundColor = (condition?: string | null): string => {
  if (!condition) return "transparent";
  const normalized = condition.toLowerCase().trim();

  if (normalized.includes("new retail") || normalized.includes("new bulk")) {
    return "var(--color-status-green-light)";
  }
  if (normalized.includes("used")) {
    return "var(--color-status-orange-light)";
  }
  if (normalized.includes("refurbished")) {
    return "var(--color-status-blue-light)";
  }
  return "transparent";
};

const getConditionTextColor = (condition?: string | null): string => {
  if (!condition) return "inherit";
  const normalized = condition.toLowerCase().trim();

  if (normalized.includes("new retail") || normalized.includes("new bulk")) {
    return "var(--color-status-green)";
  }
  if (normalized.includes("used")) {
    return "var(--color-status-orange)";
  }
  if (normalized.includes("refurbished")) {
    return "var(--color-status-blue)";
  }
  return "inherit";
};

// Format price with currency
const sym = (c?: string | null) => (c === "EUR" ? "€" : c === "GBP" ? "£" : "$");

const formatPrice = (price?: number | null, currency?: string | null) => {
  if (price === undefined || price === null) return "N/A";
  const formatted = Math.round(price).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return `${sym(currency)} ${formatted}`;
};

// Format relative date
const formatRelativeDate = (dateString?: string | null) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  const diffTime = Date.now() - date.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "1 day ago";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
  }
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

interface RecentBroadcastsProps {
  sellerId: string;
  companyName: string;
}

const RecentBroadcasts: React.FC<RecentBroadcastsProps> = ({ sellerId, companyName }) => {
  // [2025-01-12] State management
  const [broadcasts, setBroadcasts] = useState<BroadcastItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const ITEMS_PER_PAGE = 6;
  const page = 1; // Fixed to page 1 since pagination is disabled

  // [2025-01-12] Fetch broadcasts from API
  const fetchBroadcasts = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Call API with sellerId filter - fetch more broadcasts to ensure we have enough items
      const response = await listAllBroadcastsWithUsers({
        sellerId,
        page,
        limit: 50, // Fetch more broadcasts to get enough items
      });

      setBroadcasts(response.items);
    } catch (err: any) {
      console.error("Error fetching broadcasts:", err);
      setError(getErrorMessage(err, "Failed to load broadcasts"));
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  // [2025-01-12] Fetch broadcasts on mount
  useEffect(() => {
    fetchBroadcasts();
  }, [fetchBroadcasts]);

  // [2025-01-12] Flatten broadcast items from broadcast groups
  const flattenBroadcastItems = (broadcastGroups: BroadcastItem[]): FlattenedBroadcastItem[] => {
    const flattened: FlattenedBroadcastItem[] = [];

    broadcastGroups.forEach((broadcast) => {
      const items = (broadcast as BroadcastGroup).broadcast || [];
      items.forEach((item) => {
        flattened.push({
          ...item,
          broadcastType: broadcast.type,
          broadcastId: broadcast._id,
          createdAt: broadcast.createdAt,
          sentAt: broadcast.sentAt,
        });
      });
    });

    return flattened;
  };

  // Get flattened items from all loaded broadcasts
  const flattenedItems = flattenBroadcastItems(broadcasts);

  // Slice to show only ITEMS_PER_PAGE items per page
  const displayedItems = flattenedItems.slice(0, ITEMS_PER_PAGE * page);

  // [2025-01-12] Loading state
  if (loading && page === 1) {
    return (
      <div className="recent-broadcasts">
        <div className="recent-broadcasts__header">
          <h2 className="recent-broadcasts__title">Recent Broadcasts by {companyName}</h2>
        </div>
        <div className="recent-broadcasts__loading">Loading broadcasts...</div>
      </div>
    );
  }

  // [2025-01-12] Error state
  if (error && broadcasts.length === 0) {
    return (
      <div className="recent-broadcasts">
        <div className="recent-broadcasts__header">
          <h2 className="recent-broadcasts__title">Recent Broadcasts by {companyName}</h2>
        </div>
        <div className="recent-broadcasts__error">{error}</div>
      </div>
    );
  }

  return (
    <div className="recent-broadcasts">
      <div className="recent-broadcasts__header">
        <h2 className="recent-broadcasts__title">Recent Broadcasts by {companyName}</h2>
        {/* {hasMoreItems && (
          <button
            className="recent-broadcasts__view-more-header"
            onClick={handleViewMore}
            disabled={loadingMore}
          >
            View More
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M1 1L5 6L1 11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )} */}
      </div>

      {/* Empty state */}
      {displayedItems.length === 0 && !loading && (
        <div className="recent-broadcasts__empty">No broadcasts available</div>
      )}

      {/* [2026-02-10] Broadcast items table */}
      {displayedItems.length > 0 && (
        <table className="recent-broadcasts__table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Product</th>
              <th>Condition</th>
              <th>Qty</th>
              <th>Date</th>
              <th>Price</th>
            </tr>
          </thead>
          <tbody>
            {displayedItems.map((item, index) => (
              <tr key={`${item.broadcastId}-${item.itemNo}-${index}`}>
                <td>
                  <span
                    className={`badge-type badge-type--${item.broadcastType.toLowerCase()}`}
                  >
                    {item.broadcastType}
                  </span>
                </td>
                <td>
                  <span className="recent-broadcasts__manufacturer">
                    {item.manufacturer}
                  </span>
                  {(item.partNumber || item.model) && (
                    <span className="recent-broadcasts__part-number">
                      {item.partNumber || item.model}
                    </span>
                  )}
                </td>
                <td>
                  {item.condition && (
                    <span
                      className="badge-condition"
                      style={{
                        backgroundColor: getConditionBackgroundColor(item.condition),
                        color: getConditionTextColor(item.condition),
                      }}
                    >
                      {item.condition}
                    </span>
                  )}
                </td>
                <td>{item.quantity}</td>
                <td className="recent-broadcasts__date">
                  {formatRelativeDate(item.sentAt || item.createdAt)}
                </td>
                <td className="recent-broadcasts__price">
                  {formatPrice(item.price, item.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default RecentBroadcasts;
